import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Modal,
    Input,
    Button,
    Message,
    Grid,
    Progress,
    Icon,
    Divider,
    Label,
} from "semantic-ui-react";
import { toast } from "react-semantic-toasts";

import { uploadDocuments, showDocumentUploadForm } from "./actions";
import { getFileExtension, getFileName } from "../helpers";

const DocumentUpload = () => {
    const [note, setNote] = useState("");
    const [progress, setProgress] = useState(0);
    const dispatch = useDispatch();
    const {
        invoiceId,
        documentUploadForm,
        file,
        fileName: manualFileName,
        documents,
        uploadError,
    } = useSelector(({ invoiceDocument }) => invoiceDocument);

    const [documentCount] = useState(documents.length);
    const progressHandler = useRef(null);

    const [fileName, setFileName] = useState(manualFileName ?? file.name);
    const [extension, setExtension] = useState(manualFileName ?? file.name);
    const handleUpload = () => {
        dispatch(uploadDocuments(file, invoiceId, `${fileName}.${extension}`, note));
        progressHandler.current = setInterval(increaseProgress, 50);
    };

    useEffect(() => {
        const originalname = manualFileName ?? file.name;
        if (!originalname) return;

        setExtension(getFileExtension(originalname));
        setFileName(getFileName(originalname));
    }, []);

    const increaseProgress = () => setProgress(prevProgress => Math.min(90, prevProgress + 1));

    useEffect(() => {
        if (documentCount !== documents.length) {
            setProgress(100);
            clearInterval(progressHandler.current);
            setTimeout(() => {
                dispatch(showDocumentUploadForm({ documentUploadForm: false }));
            }, 1500);
        }
    }, [documents]);

    useEffect(() => {
        if (uploadError) {
            toast({
                description: `Error occurred while uploading file.`,
                time: 3000,
                type: "error",
                icon: "warning sign",
                title: "Error",
            });

            clearInterval(progressHandler.current);
            setProgress(0);
        }
    }, [uploadError]);

    const dismissModal = () => {
        if (progress > 0) return;
        dispatch(showDocumentUploadForm({ documentUploadForm: false }));
    };

    const copyOriginalName = () => {
        const originalname = manualFileName ?? file.name;
        navigator.clipboard.writeText(getFileName(originalname));

        toast({
            description: `Original file name was copied to clipboard.`,
            time: 3000,
            type: "success",
            icon: "checkmark",
            title: "Success",
        });
    };

    return (
        <Modal size="small" open={!!documentUploadForm} onClose={dismissModal}>
            <Modal.Header>
                {file.type === "application/pdf" ? "Edit document" : "Edit photo"}
            </Modal.Header>
            <Modal.Content>
                <Icon
                    name={file.type === "application/pdf" ? "file pdf" : "file image"}
                    className={`margin-auto tm-20 ${
                        file.type === "application/pdf" ? "red" : "green margin-auto"
                    }`}
                    size="massive"
                />
                <div className="text-center clickable tm-10" onClick={copyOriginalName}>
                    <Label>
                        <Icon name="copy" />
                        {manualFileName ?? file.name}
                    </Label>
                </div>
                <Divider hidden></Divider>
                <Grid>
                    <Grid.Column computer={16} mobile={16}>
                        <label>Name</label>
                        <Input
                            fluid
                            autoFocus
                            size="large"
                            value={fileName}
                            error={!fileName}
                            labelPosition="right"
                            placeholder="Set a descriptive file name"
                            label={{ basic: true, content: extension }}
                            onChange={e => setFileName(e.target.value)}
                        />
                        {!fileName && <Message error content="File name can not be empty" />}
                    </Grid.Column>
                    <Grid.Column computer={16} mobile={16}>
                        <label>Note</label>
                        <Input
                            fluid
                            size="large"
                            value={note}
                            placeholder="Set a note"
                            onChange={e => setNote(e.target.value)}
                        />
                    </Grid.Column>
                    {progress > 0 && (
                        <Grid.Column computer={16} mobile={16}>
                            <Progress percent={progress} active autoSuccess>
                                {progress === 100 ? "Upload success" : "Uploading ..."}
                            </Progress>
                        </Grid.Column>
                    )}
                </Grid>
            </Modal.Content>

            <Modal.Actions>
                <Button negative content="Cancel" onClick={dismissModal} disabled={progress > 0} />
                <Button
                    positive
                    content="Upload"
                    disabled={!fileName || progress > 0}
                    onClick={handleUpload}
                />
            </Modal.Actions>
        </Modal>
    );
};

export default DocumentUpload;
